import { useState, useRef, useEffect, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import MainNav from '@/components/layout/AppNavbar';
import IdolCard from '@/components/ui/IdolCard';
import Pagination from '@/components/ui/Pagination';
import Tabs from '@/components/ui/Tabs';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import SubscriptionPlanModal from '@/features/subscription/components/SubscriptionPlanModal';
import SubscriptionConfirmModal from '@/features/subscription/components/SubscriptionConfirmModal';
import LoadingButton from '@/components/ui/LoadingButton';
import Axios from '@/shared/api/axios';
import { formatDate } from '@/shared/utils/formatDate';

/**
 * 訂閱列表頁面
 */
const SubscriptionPage = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('active'); // 'active' or 'expired'
    const [currentPage, setCurrentPage] = useState(1);
    const [subscriptions, setSubscriptions] = useState([]);
    const [total, setTotal] = useState(0);
    const [showRenewPlanModal, setShowRenewPlanModal] = useState(false);
    const [showRenewConfirmModal, setShowRenewConfirmModal] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [selectedRenewPlan, setSelectedRenewPlan] = useState(null);
    const [isRenewing, setIsRenewing] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancelDate, setCancelDate] = useState('');
    const [currentStatusCode, setCurrentStatusCode] = useState('');

    const modalCancelConfirm = useRef(null);
    const modalCancelSuccess = useRef(null);
    const modalRenewSuccess = useRef(null);
    const statusModalRef = useRef(null);

    const itemsPerPage = 12;

    // 取得當前操作的偶像 ID
    const currentIdolId = useSelector(state => state.user.idolId);

    // 取得訂閱列表 (9-4)
    const fetchSubscriptions = async (status, page = 1) => {
        if (!currentIdolId) return;

        try {
            const response = await Axios.post('/idol/get-subscription-list', {
                idol_id: currentIdolId,
                status: status,
                page: page,
                limit: itemsPerPage
            });

            if (response.data?.status_code === 'SYSTEM_1000' && response.data.data) {
                setSubscriptions(response.data.data.subscriptions || []);
                setTotal(response.data.data.total || 0);
                setCurrentPage(page);
            } else if (response.data?.status_code) {
                setCurrentStatusCode(response.data.status_code);
                statusModalRef.current?.show();
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Failed to fetch subscriptions:', error);
            }
        }
    };

    // 初始載入
    useEffect(() => {
        if (currentIdolId) {
            fetchSubscriptions(activeTab, 1);
        }
    }, [currentIdolId]);

    // 切換 Tab
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        fetchSubscriptions(tab, 1);
    };

    // 分頁切換
    const handlePageChange = (page) => {
        fetchSubscriptions(activeTab, page);
    };

    // 取消訂閱
    const handleCancelSubscription = (idol) => {
        setSelectedSubscription(idol);
        setCancelDate(formatDate(idol.expired_at));
        modalCancelConfirm.current.show();
    };

    // 確認取消訂閱 (9-6)
    const confirmCancelSubscription = async () => {
        if (!currentIdolId || !selectedSubscription) return;

        setIsCancelling(true);
        try {
            const response = await Axios.post('/idol/cancel-subscription', {
                idol_id: currentIdolId,
                subscription_id: selectedSubscription.subscription_id
            });

            if (response.data?.status_code === 'SYSTEM_1000') {
                modalCancelSuccess.current.show();
                // 重新載入列表
                fetchSubscriptions(activeTab, currentPage);
            } else if (response.data?.status_code) {
                setCurrentStatusCode(response.data.status_code);
                statusModalRef.current?.show();
            }
        } catch (error) {
            console.error('Failed to cancel subscription:', error);
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            }
        } finally {
            setIsCancelling(false);
            modalCancelConfirm.current?.hide();
        }
    };

    // 續訂
    const handleRenewSubscription = (idol) => {
        setSelectedSubscription(idol);
        setShowRenewPlanModal(true);
    };

    // 錢包餘額抓不到就關掉扣款 modal 並顯示錯誤碼：餘額未知時不該讓使用者繼續走扣款流程
    const handleRenewWalletError = useCallback((statusCode) => {
        setShowRenewConfirmModal(false);
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
    }, []);

    const handleRenewPlanConfirm = (plan) => {
        setSelectedRenewPlan(plan);
        setShowRenewPlanModal(false);
        setShowRenewConfirmModal(true);
    };

    // 確認續訂 (9-3)
    const confirmRenewSubscription = async () => {
        if (!currentIdolId || !selectedSubscription || !selectedRenewPlan) return;

        // subscribe-idol 回應較慢，送出後鎖住確認 modal 並顯示 spinner
        setIsRenewing(true);
        try {
            const response = await Axios.post('/idol/subscribe-idol', {
                idol_id: currentIdolId,
                view_idol_id: selectedSubscription.subscribed_idol_id,
                plan_duration: selectedRenewPlan.duration
            });

            if (response.data?.status_code === 'SYSTEM_1000') {
                setShowRenewConfirmModal(false);
                modalRenewSuccess.current.show();
                // 重新載入列表
                fetchSubscriptions(activeTab, currentPage);
            } else if (response.data?.status_code) {
                setShowRenewConfirmModal(false);
                setCurrentStatusCode(response.data.status_code);
                statusModalRef.current?.show();
            }
        } catch (error) {
            console.error('Failed to renew subscription:', error);
            setShowRenewConfirmModal(false);
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            }
        } finally {
            setIsRenewing(false);
        }
    };

    return (
        <>
            <MainNav />
            <Container fluid="lg" className="nav-padding">
                <Row className="pt-4">
                    <Col xs={12}>
                        <h3 className="mb-4">{t('my_subscription')}</h3>

                        {/* Tab 切換 */}
                        <Tabs
                            tabs={[
                                { key: 'active', label: t('subscribed') },
                                { key: 'expired', label: t('expired') }
                            ]}
                            activeTab={activeTab}
                            onChange={handleTabChange}
                            className="mb-4"
                        />

                        {/* 訂閱列表 */}
                        {subscriptions.length > 0 ? (
                            <div className="cards-grid mb-4 mt-5">
                                {subscriptions.map((idol) => (
                                    <IdolCard
                                        key={idol.subscription_id}
                                        idol={idol}
                                        showSubscriptionInfo={true}
                                        showOnline
                                        onCancelSubscription={handleCancelSubscription}
                                        onRenewSubscription={handleRenewSubscription}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-5 text-muted mb-4">
                                {activeTab === 'active' ? t('no_active_subscriptions') : t('no_expired_subscriptions')}
                            </div>
                        )}

                        {/* 分頁 */}
                        {total > itemsPerPage && (
                            <div className="d-flex justify-content-center">
                                <Pagination
                                    total={total}
                                    limit={itemsPerPage}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                    scrollMode="window"
                                />
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>

            {/* 取消訂閱確認 Modal */}
            <MessageModal ref={modalCancelConfirm} status="warning" size="md" locked={isCancelling}>
                <h5 className="mb-3">{t('confirm_cancel_subscription')}</h5>
                <p className="mb-4">{t('cancel_subscription_notice', { date: cancelDate })}</p>
                <div className="text-center">
                    <button
                        type="button"
                        className="btn btn-neutral rounded-pill me-3"
                        onClick={() => modalCancelConfirm.current.hide()}
                        disabled={isCancelling}
                    >
                        {t('cancel')}
                    </button>
                    <LoadingButton
                        className="btn btn-primary rounded-pill"
                        onClick={confirmCancelSubscription}
                        isLoading={isCancelling}
                    >
                        {t('confirm')}
                    </LoadingButton>
                </div>
            </MessageModal>

            {/* 取消訂閱成功 Modal */}
            <MessageModal ref={modalCancelSuccess} status="success">
                <p>{t('cancel_subscription_success')}</p>
            </MessageModal>

            {/* 續訂成功 Modal */}
            <MessageModal ref={modalRenewSuccess} status="success">
                <p>{t('renew_success')}</p>
            </MessageModal>

            {/* 續訂方案選擇 Modal */}
            <SubscriptionPlanModal
                show={showRenewPlanModal}
                onHide={() => setShowRenewPlanModal(false)}
                onConfirm={handleRenewPlanConfirm}
                mode="renew"
                idolId={selectedSubscription?.subscribed_idol_id}
                idolName={selectedSubscription?.subscribed_idol_name}
            />

            {/* 續訂確認 Modal */}
            <SubscriptionConfirmModal
                show={showRenewConfirmModal}
                onHide={() => setShowRenewConfirmModal(false)}
                onConfirm={confirmRenewSubscription}
                onError={handleRenewWalletError}
                mode="renew"
                idolName={selectedSubscription?.subscribed_idol_name}
                selectedPlan={selectedRenewPlan}
                isLoading={isRenewing}
            />

            {/* Status Modal */}
            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
        </>
    );
};

export default SubscriptionPage;
